import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, ExternalLink, RefreshCw } from "lucide-react";
import { Gate } from "@/components/wallet/Gate";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { XrpPriceChart } from "@/components/wallet/XrpPriceChart";
import {
  compactNumber,
  compactUsd,
  signedPercent,
} from "@/lib/wallet/markets";
import {
  ledgerPulseQuery,
  xrpEtfQuery,
  xrpMarketQuery,
  xrplEcosystemQuery,
  xrplRwaQuery,
} from "@/lib/wallet/market-queries";

function MarketsError({ error }: { error: Error }) {
  return <div role="alert" className="panel p-4 text-[12px] text-danger">Markets unavailable: {error.message}</div>;
}

function MarketsSkeleton() {
  return (
    <div className="space-y-4" aria-label="Loading XRPL markets">
      <section className="panel min-h-48 space-y-5 p-5"><Skeleton className="h-3 w-20" /><Skeleton className="h-10 w-36" /><Skeleton className="h-14 w-full" /></section>
      <div className="grid grid-cols-2 gap-3">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
      {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-52 rounded-xl" />)}
    </div>
  );
}

export const Route = createFileRoute("/markets")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "XRPL markets — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Live XRP price, market cap and volume with spot XRP ETF listings, XRPL tokenized real-world assets and XRP Ledger network health.",
      },
      { property: "og:title", content: "XRPL markets — Cobalt Wallet" },
      {
        property: "og:description",
        content: "XRP price, spot XRP ETFs, XRPL real-world assets and ledger health in one screen.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(xrpMarketQuery),
      context.queryClient.ensureQueryData(xrplEcosystemQuery),
      context.queryClient.ensureQueryData(xrplRwaQuery),
      context.queryClient.ensureQueryData(ledgerPulseQuery),
      context.queryClient.ensureQueryData(xrpEtfQuery),
    ]),
  pendingMs: 100,
  pendingMinMs: 300,
  pendingComponent: MarketsSkeleton,
  errorComponent: MarketsError,
  notFoundComponent: () => <div className="panel p-4 text-[12px] text-muted">Market data was not found.</div>,
  component: () => (
    <Gate>
      <MarketsScreen />
    </Gate>
  ),
});

function Delta({ value }: { value: number | null }) {
  if (value === null) return <span className="readout text-[10px] text-faint">—</span>;
  const up = value >= 0;
  return (
    <span
      className={`readout inline-flex items-center gap-1 text-[10px] ${
        up ? "text-success" : "text-danger"
      }`}
    >
      {up ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
      {signedPercent(value)}
    </span>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: React.ReactNode;
}) {
  return (
    <div className="panel p-3">
      <p className="label-instrument">{label}</p>
      <p className="readout mt-2 text-[15px] font-medium tracking-tight">{value}</p>
      {sub ? <div className="mt-1">{sub}</div> : null}
    </div>
  );
}

function MarketsScreen() {
  const xrp = useSuspenseQuery(xrpMarketQuery);
  const ecosystem = useSuspenseQuery(xrplEcosystemQuery);
  const rwa = useSuspenseQuery(xrplRwaQuery);
  const pulse = useSuspenseQuery(ledgerPulseQuery);
  const etfs = useSuspenseQuery(xrpEtfQuery);

  const m = xrp.data;
  const refreshing = xrp.isFetching || ecosystem.isFetching || rwa.isFetching || pulse.isFetching || etfs.isFetching;

  const timeFormat = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });

  const priceUpdatedAt = xrp.dataUpdatedAt ? timeFormat.format(new Date(xrp.dataUpdatedAt)) : "—";

  const updatedAt = etfs.data?.updatedAt
    ? timeFormat.format(new Date(etfs.data.updatedAt))
    : "Unavailable";


  return (
    <div className="animate-rise space-y-4 transition-opacity duration-300">
      <h1 className="sr-only">XRPL market overview</h1>
      <section className="panel overflow-hidden p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="label-instrument">XRP / USD</p>
            <p className="readout mt-2 text-[30px] font-medium leading-none tracking-tight">
              {m ? `$${m.price.toFixed(m.price < 10 ? 4 : 2)}` : xrp.isLoading ? "…" : "—"}
            </p>
            <div className="mt-2 flex items-center gap-3">
              <Delta value={m?.change24h ?? null} />
              <span className="readout text-[10px] text-faint">24h</span>
              {m?.marketCapRank ? (
                <span className="readout text-[10px] text-faint">rank #{m.marketCapRank}</span>
              ) : null}
            </div>
            <p className="readout mt-2 flex items-center gap-1.5 text-[9px] text-faint" aria-live="polite">
              <span
                className={`size-1.5 rounded-full ${xrp.isFetching ? "animate-pulse bg-accent" : "bg-success"}`}
                aria-hidden="true"
              />
              {xrp.isFetching ? "Updating price…" : `Price updated ${priceUpdatedAt}`}
            </p>

          </div>
          <Button
            type="button"
            onClick={() => {
              void xrp.refetch();
              void ecosystem.refetch();
              void rwa.refetch();
              void pulse.refetch();
              void etfs.refetch();
            }}
            variant="outline"
            size="icon"
            className="btn-quiet size-9 shrink-0 rounded-lg"
            aria-label="Refresh market data"
          >
            <RefreshCw className={`mx-auto size-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>

        <div className="mt-5">
          <XrpPriceChart />
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2">
          {[
            { label: "1h", value: m?.change1h ?? null },
            { label: "24h", value: m?.change24h ?? null },
            { label: "7d", value: m?.change7d ?? null },
            { label: "30d", value: m?.change30d ?? null },
          ].map((p) => (
            <div key={p.label} className="rounded-lg bg-elevated p-2 text-center">
              <p className="readout text-[9px] uppercase tracking-[0.15em] text-faint">{p.label}</p>
              <div className="mt-1 flex justify-center">
                <Delta value={p.value} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Market cap" value={compactUsd(m?.marketCap)} />
        <Stat label="24h volume" value={compactUsd(m?.volume24h)} />
        <Stat
          label="24h range"
          value={m?.low24h && m?.high24h ? `$${m.low24h.toFixed(3)} – $${m.high24h.toFixed(3)}` : "—"}
        />
        <Stat label="Fully diluted" value={compactUsd(m?.fdv)} />
        <Stat
          label="Circulating XRP"
          value={compactNumber(m?.circulating)}
          sub={
            m?.total ? (
              <span className="readout text-[10px] text-faint">of {compactNumber(m.total)}</span>
            ) : null
          }
        />
        <Stat
          label="All-time high"
          value={m?.ath ? `$${m.ath.toFixed(2)}` : "—"}
          sub={<Delta value={m?.athChange ?? null} />}
        />
      </div>

      <section className="panel p-4">
        <div className="mb-3">
          <h2 className="section-title">Real-world assets</h2>
          <p className="mt-1 text-[11px] text-faint">Tokenized value connected specifically to the XRP Ledger.</p>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-elevated p-3">
            <p className="readout text-[9px] uppercase tracking-[0.15em] text-faint">Distributed</p>
            <p className="readout mt-1 text-[14px] font-medium">
              {compactUsd(rwa.data?.distributedTvl)}
            </p>
          </div>
          <div className="rounded-lg bg-elevated p-3">
            <p className="readout text-[9px] uppercase tracking-[0.15em] text-faint">Represented</p>
            <p className="readout mt-1 text-[14px] font-medium">
              {compactUsd(rwa.data?.representedTvl)}
            </p>
          </div>
          <div className="rounded-lg bg-elevated p-3">
            <p className="readout text-[9px] uppercase tracking-[0.15em] text-faint">XRPL DeFi TVL</p>
            <p className="readout mt-1 text-[14px] font-medium">{compactUsd(rwa.data?.xrplTvl)}</p>
          </div>
        </div>
        <p className="readout mt-2 text-[9px] leading-relaxed text-faint">
          Distributed is the value of tokenized assets issued directly on the XRPL. Represented is
          the full underlying real-world asset value of those issuers, across every chain they
          operate on.
        </p>
        {(rwa.data?.issuers ?? []).length ? (
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <span className="readout text-[9px] uppercase tracking-[0.12em] text-faint">
                Issuer
              </span>
              <span className="readout shrink-0 text-[9px] uppercase tracking-[0.12em] text-faint">
                Distributed / Represented
              </span>
            </div>
            {(rwa.data?.issuers ?? []).map((issuer) => (
              <div key={issuer.name} className="flex items-center justify-between gap-3">
                <span className="min-w-0 truncate text-[11px]">
                  {issuer.name}
                  <span className="readout ml-2 text-[9px] uppercase tracking-[0.12em] text-faint">
                    {issuer.category}
                  </span>
                </span>
                <span className="readout shrink-0 text-[11px] text-muted">
                  {compactUsd(issuer.distributed)}
                  <span className="text-faint"> / {compactUsd(issuer.represented)}</span>
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="readout mt-3 text-[10px] text-faint">
            {rwa.isLoading
              ? "Loading XRPL tokenized-asset data…"
              : "No XRPL-deployed tokenized asset protocols are reporting TVL right now."}
          </p>
        )}
      </section>

      <section className="panel overflow-hidden p-4">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
          <div className="min-w-0">
            <h2 className="section-title">XRP ETFs & ETPs</h2>
            <p className="mt-1 text-[11px] text-faint">Live fund assets and daily flows.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="icon" onClick={() => void etfs.refetch()} aria-label="Refresh XRP ETF tracker" className="btn-quiet size-8 rounded-lg">
              <RefreshCw className={`size-3.5 ${etfs.isFetching ? "animate-spin" : ""}`} />
            </Button>
            <a href={etfs.data?.sourceUrl ?? "https://etfllama.com/crypto/xrp"} target="_blank" rel="noreferrer" aria-label="Open XRP ETF tracker" className="grid size-8 shrink-0 place-items-center rounded-lg border border-border text-muted transition-colors hover:text-accent">
              <ExternalLink className="size-3.5" />
            </a>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-elevated p-3">
            <p className="label-instrument">Total AUM</p>
            <p className="readout mt-1.5 text-[17px] font-medium">{compactUsd(etfs.data?.totalAumUsd)}</p>
          </div>
          <div className="rounded-lg bg-elevated p-3">
            <p className="label-instrument">Daily flow</p>
            <p className={`readout mt-1.5 text-[17px] font-medium ${(etfs.data?.totalFlow1dUsd ?? 0) >= 0 ? "text-success" : "text-danger"}`}>
              {compactUsd(etfs.data?.totalFlow1dUsd)}
            </p>
          </div>
        </div>
        <div className="mt-3 space-y-1.5">
          {(etfs.data?.funds ?? []).map((etf) => (
            <div
              key={etf.id}
              className="interactive-lift grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-transparent bg-elevated p-3"
            >
              <div className="min-w-0">
                <p className="text-[12px] font-semibold">{etf.ticker}</p>
                <p className="mt-0.5 truncate text-[10px] text-faint">{etf.provider} · {etf.region}</p>
              </div>
              <div className="shrink-0 text-right">
                <p className="readout text-[11px]">{compactUsd(etf.aumUsd)}</p>
                <p className={`readout mt-0.5 text-[9px] ${(etf.flow1dUsd ?? 0) >= 0 ? "text-success" : "text-danger"}`}>
                  {compactUsd(etf.flow1dUsd)} today
                </p>
              </div>
            </div>
          ))}
        </div>
        {!etfs.isLoading && !etfs.data ? (
          <p className="mt-3 rounded-lg border border-border p-3 text-[10px] leading-relaxed text-muted">
            Live ETF figures are temporarily unavailable. Cobalt does not substitute stale or estimated fund data.
          </p>
        ) : null}
        <div className="readout mt-3 flex items-center justify-between gap-3 text-[9px] text-faint">
          <span>Source: ETF Llama</span>
          <span aria-live="polite">Last updated {updatedAt}</span>
        </div>
      </section>

      <section className="panel p-4">
        <h2 className="section-title">XRPL ecosystem</h2>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-elevated p-3">
            <p className="readout text-[9px] uppercase tracking-[0.15em] text-faint">Market cap</p>
            <p className="readout mt-1 text-[12px] font-medium">
              {compactUsd(ecosystem.data?.marketCap)}
            </p>
          </div>
          <div className="rounded-lg bg-elevated p-3">
            <p className="readout text-[9px] uppercase tracking-[0.15em] text-faint">24h volume</p>
            <p className="readout mt-1 text-[12px] font-medium">
              {compactUsd(ecosystem.data?.volume24h)}
            </p>
          </div>
          <div className="rounded-lg bg-elevated p-3">
            <p className="readout text-[9px] uppercase tracking-[0.15em] text-faint">24h change</p>
            <div className="mt-1.5">
              <Delta value={ecosystem.data?.change24h ?? null} />
            </div>
          </div>
        </div>
      </section>

      <section className="panel p-4">
        <h2 className="section-title">Ledger health</h2>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[
            { label: "Validated ledger", value: compactNumber(pulse.data?.ledgerIndex) },
            { label: "Base fee", value: pulse.data?.baseFeeXrp ? `${pulse.data.baseFeeXrp} XRP` : "—" },
            {
              label: "Account reserve",
              value: pulse.data?.reserveBaseXrp ? `${pulse.data.reserveBaseXrp} XRP` : "—",
            },
            {
              label: "Owner reserve",
              value: pulse.data?.reserveIncXrp ? `${pulse.data.reserveIncXrp} XRP` : "—",
            },
            { label: "Load factor", value: pulse.data?.loadFactor?.toString() ?? "—" },
            { label: "rippled", value: pulse.data?.version ?? "—" },
          ].map((row) => (
            <div key={row.label} className="rounded-lg bg-elevated p-3">
              <p className="readout text-[9px] uppercase tracking-[0.15em] text-faint">
                {row.label}
              </p>
              <p className="readout mt-1 text-[12px] font-medium">{row.value}</p>
            </div>
          ))}
        </div>
      </section>

      <p className="readout px-1 text-[9px] leading-relaxed text-faint">
        Market data from CoinGecko, DefiLlama and ETF Llama; ledger data direct from XRPL mainnet. Read-only:
        no wallet address or balance is ever sent to a market data provider.
      </p>
    </div>
  );
}
